/**
 * shopify-cart-sync.js — v4.5.0
 *
 * Shopify storefront cart sync.
 *
 * USER ID RESOLUTION (no hardcoded IDs):
 *   1. ?uId= or ?userId= in URL  → save to localStorage, use it
 *   2. localStorage cs_user_id   → already have one, use it
 *   3. Neither                   → auto-generate UUIDv4, save to localStorage
 *
 * On arrival from WP checkout URL (?uId=<uuid>):
 *   1. Save userId from URL to localStorage
 *   2. Stamp cs_user_id onto Shopify cart attributes
 *   3. Fetch cart from DO via HTTP GET /cart?userId=<uuid>
 *   4. Apply if DO is newer/different
 *   5. Connect WebSocket for ongoing sync
 *
 * Three-layer loop prevention:
 *   1. sourceId      — ignore messages we sent ourselves
 *   2. cartHash      — ignore if incoming cart state is identical
 *   3. lastUpdatedAt — ignore if incoming is older than our last update
 *
 * Cart update method: clear + add (guarantees exact DO cart state, no stale diffs).
 * UI refresh method:  Dawn Section Rendering API — no page reload.
 * Toast notifications: shown after every background cart update so the user is aware.
 */
(function () {
  'use strict';

  var LOG = {
    info:  function (m, d) { d !== undefined ? console.log('%c[Shopify-CartSync] ' + m, 'color:#2980b9;font-weight:bold;', d)  : console.log('%c[Shopify-CartSync] ' + m, 'color:#2980b9;font-weight:bold;'); },
    ok:    function (m, d) { d !== undefined ? console.log('%c[Shopify-CartSync] OK ' + m, 'color:#27ae60;font-weight:bold;', d) : console.log('%c[Shopify-CartSync] OK ' + m, 'color:#27ae60;font-weight:bold;'); },
    warn:  function (m, d) { d !== undefined ? console.warn('%c[Shopify-CartSync] WARN ' + m, 'color:#e67e22;font-weight:bold;', d) : console.warn('%c[Shopify-CartSync] WARN ' + m, 'color:#e67e22;font-weight:bold;'); },
    error: function (m, d) { d !== undefined ? console.error('%c[Shopify-CartSync] ERR ' + m, 'color:#e74c3c;font-weight:bold;', d) : console.error('%c[Shopify-CartSync] ERR ' + m, 'color:#e74c3c;font-weight:bold;'); },
    push:  function (m, d) { d !== undefined ? console.log('%c[Shopify-CartSync] >> ' + m, 'color:#8e44ad;font-weight:bold;', d) : console.log('%c[Shopify-CartSync] >> ' + m, 'color:#8e44ad;font-weight:bold;'); },
    recv:  function (m, d) { d !== undefined ? console.log('%c[Shopify-CartSync] << ' + m, 'color:#16a085;font-weight:bold;', d) : console.log('%c[Shopify-CartSync] << ' + m, 'color:#16a085;font-weight:bold;'); },
  };

  // ── CONFIG ────────────────────────────────────────────────────────────
  var WORKER_URL = 'https://cart-sync-do.applabs.workers.dev';
  var WP_DOMAIN  = 'https://cercacultivation.com';

  // ── USER ID RESOLUTION ─────────────────────────────────────────────────
  var LS_USER_ID_KEY = 'cs_user_id';

  function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  function isValidUUID(str) {
    return /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(str);
  }

  // Read ?uId= or ?userId= from URL
  var URL_USER_ID = (function () {
    var p = new URLSearchParams(window.location.search);
    return p.get('uId') || p.get('userId') || null;
  })();

  // Resolve USER_ID: URL > localStorage > auto-generate
  var USER_ID = (function () {
    if (URL_USER_ID && isValidUUID(URL_USER_ID)) {
      var existing = localStorage.getItem(LS_USER_ID_KEY);
      if (existing !== URL_USER_ID) {
        LOG.ok('URL userId received — saving to localStorage old:' + (existing || 'none') + ' new:' + URL_USER_ID);
        localStorage.setItem(LS_USER_ID_KEY, URL_USER_ID);
      } else {
        LOG.info('URL userId matches localStorage — no change');
      }
      return URL_USER_ID;
    }
    var stored = localStorage.getItem(LS_USER_ID_KEY);
    if (stored && isValidUUID(stored)) {
      LOG.info('Using existing userId from localStorage: ' + stored);
      return stored;
    }
    var fresh = generateUUID();
    localStorage.setItem(LS_USER_ID_KEY, fresh);
    LOG.ok('Auto-generated new userId: ' + fresh);
    return fresh;
  })();

  var WS_URL          = WORKER_URL.replace(/^http/, 'ws') + '/ws';
  var HTTP_UPDATE_URL = WORKER_URL + '/cart/update';
  var SOURCE_ID       = 'shopify_' + Math.random().toString(36).substring(2, 10);

  LOG.ok('Loaded | userId:' + USER_ID + ' sourceId:' + SOURCE_ID);

  // ── EARLY OVERLAY — inject before first paint if arriving from WP ─────
  // Must run synchronously here (not inside DOMContentLoaded) so the overlay
  // is present before the browser paints the empty cart skeleton.
  if (URL_USER_ID) {
    (function earlyOverlay() {
      var style = document.createElement('style');
      style.id  = 'cs-early-overlay-style';
      style.textContent = [
        '#cs-arrival-overlay{',
        '  position:fixed;inset:0;z-index:99999;',
        '  background:rgb(var(--color-background,255,255,255));',
        '  display:flex;flex-direction:column;',
        '  align-items:center;justify-content:center;gap:1.6rem;',
        '  opacity:1;transition:opacity 0.4s ease;',
        '}',
        '#cs-arrival-overlay.cs-fade-out{opacity:0;pointer-events:none;}',
        '@keyframes cs-spin{to{transform:rotate(360deg)}}',
        '@keyframes cs-dash{0%{stroke-dashoffset:220}50%{stroke-dashoffset:55}100%{stroke-dashoffset:220}}',
        '#cs-arrival-overlay .cs-ring-svg{width:5.2rem;height:5.2rem;overflow:visible;}',
        '#cs-arrival-overlay .cs-ring-track{opacity:0.12;}',
        '#cs-arrival-overlay .cs-ring-fill{',
        '  stroke-dasharray:220;stroke-dashoffset:220;',
        '  transform-origin:50% 50%;transform:rotate(-90deg);',
        '  animation:cs-spin 1.4s linear infinite,cs-dash 1.6s ease-in-out infinite;',
        '}',
        '#cs-arrival-overlay .cs-overlay-text{',
        '  font-family:var(--font-body-family,sans-serif);',
        '  font-size:1.4rem;',
        '  color:rgb(var(--color-foreground,18,18,18));',
        '  opacity:0.65;letter-spacing:0.04em;',
        '}',
      ].join('');

      var overlay = document.createElement('div');
      overlay.id = 'cs-arrival-overlay';
      overlay.setAttribute('role', 'status');
      overlay.setAttribute('aria-label', 'Loading your cart\u2026');
      overlay.innerHTML = '<svg class="cs-ring-svg" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">'
        + '<circle class="cs-ring-track" cx="40" cy="40" r="35" stroke="rgb(var(--color-foreground,18,18,18))" stroke-width="5"/>'
        + '<circle class="cs-ring-fill" cx="40" cy="40" r="35" stroke="rgb(var(--color-foreground,18,18,18))" stroke-width="5" stroke-linecap="round"/>'
        + '</svg>'
        + '<p class="cs-overlay-text">Please wait, loading your cart\u2026</p>';

      // Inject as early as possible — <head> if available, else <html>
      var target = document.head || document.documentElement;
      target.appendChild(style);
      document.documentElement.appendChild(overlay);

      LOG.ok('Early overlay injected (pre-paint)');
    })();
  }

  // ── State ─────────────────────────────────────────────────────────────
  var ws             = null;
  var reconnectDelay = 2000;
  var isStopped      = false;
  var syncDebounce   = null;

  var isSyncingFromDO      = false;
  var pendingApply         = null;
  var isApplying           = false;
  var SYNC_COOLDOWN_MS     = 500;
  var lastApplyCompletedAt = 0;

  // ── Cart hash (djb2) — MUST be identical to WP side ──────────────────
  function computeCartHash(items) {
    if (!items || items.length === 0) return 'empty';
    var str = items
      .map(function (i) {
        var vid = i.variant_id !== undefined ? i.variant_id : (i.variantId || '');
        return String(vid) + ':' + String(i.quantity);
      })
      .sort()
      .join('|');
    return djb2(str);
  }

  function djb2(str) {
    var hash = 5381;
    for (var i = 0; i < str.length; i++) {
      hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
      hash = hash >>> 0;
    }
    return hash.toString(16);
  }

  // ── Timestamp + hash storage ──────────────────────────────────────────
  function nowSeconds() { return Math.floor(Date.now() / 1000); }
  function getLocalTs()   { return parseInt(localStorage.getItem('cs_cart_updated_at') || '0', 10); }
  function getLocalHash() { return localStorage.getItem('cs_cart_hash') || ''; }

  function setLocalMeta(ts, hash) {
    localStorage.setItem('cs_cart_updated_at', String(ts));
    localStorage.setItem('cs_cart_hash', hash);
    LOG.info('Local meta updated ts:' + ts + ' hash:' + hash);
  }

  // ── Write userId into Shopify cart attributes ─────────────────────────
  async function writeUserIdToCartAttributes() {
    LOG.info('writeUserIdToCartAttributes: stamping cs_user_id=' + USER_ID);
    try {
      isSyncingFromDO = true;
      var res  = await fetch('/cart/update.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: { cs_user_id: USER_ID } }),
      });
      var data = await res.json();
      if (data.status && data.status >= 400) {
        LOG.error('writeUserIdToCartAttributes: error', data);
      } else {
        var saved = (data.attributes && data.attributes.cs_user_id) || 'not found';
        LOG.ok('writeUserIdToCartAttributes done sent:' + USER_ID + ' saved:' + saved);
      }
    } catch (err) {
      LOG.error('writeUserIdToCartAttributes: failed', err);
    } finally {
      isSyncingFromDO = false;
    }
  }

  // ── Fetch cart from DO on arrival from WP checkout URL ───────────────
  async function fetchCartFromDO(forceApply) {
    LOG.info('fetchCartFromDO: fetching for userId:' + USER_ID + (forceApply ? ' (force)' : ''));
    try {
      var res  = await fetch(WORKER_URL + '/cart?uId=' + encodeURIComponent(USER_ID));
      var data = await res.json();

      var doCart = data.cart          || [];
      var doHash = data.cartHash      || computeCartHash(doCart);
      var doTs   = data.lastUpdatedAt || 0;
      var localHash = getLocalHash();
      var localTs   = getLocalTs();

      LOG.info('fetchCartFromDO doItems:' + doCart.length + ' doHash:' + doHash + ' doTs:' + doTs + ' localHash:' + localHash + ' localTs:' + localTs);

      if (!forceApply) {
        if (doHash === localHash) { LOG.ok('fetchCartFromDO: already in sync'); return; }
        if (doTs <= localTs && localHash !== 'empty') { LOG.warn('fetchCartFromDO: DO is older — keeping local'); return; }
      } else {
        // Force path: skip timestamp/hash guards. DO is the source of truth.
        // Still skip if DO cart is genuinely empty AND local is also empty — nothing to do.
        if (doCart.length === 0 && localHash === 'empty') { LOG.ok('fetchCartFromDO: both empty, nothing to apply'); return; }
      }

      LOG.ok('fetchCartFromDO: applying DO cart items:' + doCart.length);
      // On force apply, stamp a fresh timestamp so the apply isn't blocked downstream
      applyCartToShopify(doCart, forceApply ? nowSeconds() : doTs, doHash);

    } catch (err) {
      LOG.error('fetchCartFromDO: failed', err);
    }
  }

  // ── Apply incoming cart to Shopify (clear + add, exact state) ─────────
  function applyCartToShopify(incomingItems, incomingTs, incomingHash) {
    setLocalMeta(incomingTs, incomingHash);
    if (isApplying) {
      LOG.warn('applyCartToShopify: queuing ts:' + incomingTs + ' hash:' + incomingHash);
      pendingApply = { items: incomingItems, ts: incomingTs, hash: incomingHash };
      return;
    }
    isApplying = true;
    if (!_overlayEl) showSyncStrip();
    _doApply(incomingItems, incomingTs, incomingHash);
  }

  async function _doApply(incomingItems, incomingTs, incomingHash) {
    LOG.recv('_doApply ' + incomingItems.length + ' items ts:' + incomingTs);
    isSyncingFromDO = true;

    try {
      // Step 1: Clear existing Shopify cart entirely
      await fetch('/cart/clear.js', { method: 'POST' });
      LOG.info('_doApply: cart cleared');

      // Step 2: Add all incoming items fresh (if any)
      if (incomingItems.length > 0) {
        var toAdd = incomingItems.map(function (i) {
          return { id: i.variantId, quantity: i.quantity };
        });
        var addRes  = await fetch('/cart/add.js', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: toAdd }),
        });
        var addData = await addRes.json();
        if (addData.status && addData.status >= 400) {
          LOG.error('_doApply /cart/add.js error', addData);
        } else {
          LOG.ok('_doApply /cart/add.js added ' + (addData.items || []).length + ' items');
        }
      }

      LOG.ok('_doApply complete — refreshing cart UI');
      await refreshThemeCart(incomingItems);

    } catch (err) {
      LOG.error('_doApply failed', err);
    } finally {
      isSyncingFromDO      = false;
      isApplying           = false;
      lastApplyCompletedAt = Date.now();
      if (pendingApply) {
        var next = pendingApply; pendingApply = null;
        LOG.info('Applying queued state ts:' + next.ts);
        applyCartToShopify(next.items, next.ts, next.hash);
      }
    }
  }

  // ── Handle incoming WS message ────────────────────────────────────────
  function handleIncoming(msg) {
    if (msg.sourceId === SOURCE_ID) { LOG.info('Layer 1 blocked — own echo'); return; }

    var incomingHash = msg.cartHash || computeCartHash(msg.cart || []);
    var localHash    = getLocalHash();

    if (incomingHash === localHash) { LOG.info('Layer 2 blocked — identical hash ' + incomingHash); return; }

    var incomingTs = msg.lastUpdatedAt || 0;
    var localTs    = getLocalTs();

    if (incomingTs <= localTs) { LOG.warn('Layer 3 blocked — stale ts ' + incomingTs + ' <= ' + localTs); return; }

    LOG.recv('All 3 layers passed hash:' + localHash + '->' + incomingHash + ' ts:' + localTs + '->' + incomingTs);
    applyCartToShopify(msg.cart || [], incomingTs, incomingHash);
  }

  // ── Push Shopify cart to DO ───────────────────────────────────────────
  function scheduleSync(reason) {
    clearTimeout(syncDebounce);
    syncDebounce = setTimeout(function () { doSync(reason); }, 800);
  }

  async function doSync(reason) {
    if (isSyncingFromDO) { LOG.info('doSync skipped syncing (' + reason + ')'); return; }
    var msSince = Date.now() - lastApplyCompletedAt;
    if (msSince < SYNC_COOLDOWN_MS) { LOG.info('doSync skipped cooldown ' + Math.round(msSince) + 'ms (' + reason + ')'); return; }

    // Show a lightweight loading strip so the user knows the cart is initialising
    if (!_overlayEl) showSyncStrip();

    try {
      var res  = await fetch('/cart.js');
      var cart = await res.json();

      var items = (cart.items || []).map(function (item) {
        return {
          productId:   item.product_id,
          variantId:   item.variant_id,
          productName: item.title,
          price:       item.price / 100,
          quantity:    item.quantity,
          image:       (item.featured_image && item.featured_image.url) ? item.featured_image.url : (item.image || ''),
          variantName: item.variant_title || '',
          vendor:      item.vendor || '',
        };
      });

      var hash = computeCartHash(items);
      if (hash === getLocalHash()) { LOG.info('doSync: hash unchanged ' + hash + ' skipping'); return; }

      var ts = nowSeconds();
      setLocalMeta(ts, hash);

      var payload = { uId: USER_ID, sourceId: SOURCE_ID, cart: items, cartHash: hash, lastUpdatedAt: ts };
      LOG.push('doSync (' + reason + ') items:' + items.length + ' hash:' + hash);

      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify(Object.assign({ type: 'cart_update' }, payload)));
        LOG.push('Sent via WS');
        return;
      }

      var httpRes = await fetch(HTTP_UPDATE_URL, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      LOG.push('HTTP fallback response', await httpRes.json());

    } catch (err) { LOG.error('doSync failed', err); }
    finally { hideSyncStrip(600); }
  }

  // ── Refresh theme cart UI via Dawn Section Rendering API ──────────────
  // incomingItems is passed in so we can build a meaningful toast message.
  async function refreshThemeCart(incomingItems) {
    LOG.ok('refreshThemeCart: fetching Dawn sections');

    var sectionIds = [
      'cart-icon-bubble',
      'cart-live-region-text',
    ];

    var mainCartItems  = document.getElementById('main-cart-items');
    var mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartItems  && mainCartItems.dataset.id)  sectionIds.push(mainCartItems.dataset.id);
    if (mainCartFooter && mainCartFooter.dataset.id) sectionIds.push(mainCartFooter.dataset.id);

    sectionIds.push('cart-drawer');

    try {
      var url    = window.location.pathname + '?sections=' + sectionIds.join(',');
      var res    = await fetch(url);
      var data   = await res.json();
      var parser = new DOMParser();

      // ── cart-icon-bubble ──────────────────────────────────────────────
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble && data['cart-icon-bubble']) {
        var parsed    = parser.parseFromString(data['cart-icon-bubble'], 'text/html');
        var newBubble = parsed.querySelector('.cart-count-bubble');
        var oldBubble = bubble.querySelector('.cart-count-bubble');
        if (newBubble && oldBubble) {
          oldBubble.innerHTML = newBubble.innerHTML;
        } else {
          var section = parsed.querySelector('.shopify-section');
          if (section) bubble.innerHTML = section.innerHTML;
        }
      }

      // ── cart-live-region-text ─────────────────────────────────────────
      var liveRegion = document.getElementById('cart-live-region-text') ||
                       document.getElementById('CartDrawer-LiveRegionText');
      if (liveRegion && data['cart-live-region-text']) {
        var parsed  = parser.parseFromString(data['cart-live-region-text'], 'text/html');
        var section = parsed.querySelector('.shopify-section');
        if (section) liveRegion.innerHTML = section.innerHTML;
      }

      // ── main-cart-items ───────────────────────────────────────────────
      if (mainCartItems && mainCartItems.dataset.id && data[mainCartItems.dataset.id]) {
        var parsed      = parser.parseFromString(data[mainCartItems.dataset.id], 'text/html');
        var newContents = parsed.querySelector('.js-contents');
        var oldContents = mainCartItems.querySelector('.js-contents');
        if (newContents && oldContents) {
          oldContents.innerHTML = newContents.innerHTML;
          LOG.ok('refreshThemeCart: main-cart-items .js-contents replaced');
        } else {
          var newSection = parsed.querySelector('#main-cart-items');
          if (newSection) {
            mainCartItems.innerHTML = newSection.innerHTML;
            LOG.ok('refreshThemeCart: main-cart-items innerHTML replaced (fallback)');
          }
        }
      }

      // ── main-cart-footer ──────────────────────────────────────────────
      if (mainCartFooter && mainCartFooter.dataset.id && data[mainCartFooter.dataset.id]) {
        var parsed      = parser.parseFromString(data[mainCartFooter.dataset.id], 'text/html');
        var newContents = parsed.querySelector('.js-contents');
        var oldContents = mainCartFooter.querySelector('.js-contents');
        if (newContents && oldContents) {
          oldContents.innerHTML = newContents.innerHTML;
        } else {
          var newSection = parsed.querySelector('#main-cart-footer');
          if (newSection) mainCartFooter.innerHTML = newSection.innerHTML;
        }
      }

      // ── cart-drawer ───────────────────────────────────────────────────
      var cartDrawer = document.getElementById('CartDrawer');
      if (cartDrawer && data['cart-drawer']) {
        var parsed      = parser.parseFromString(data['cart-drawer'], 'text/html');
        var drawerInner = cartDrawer.querySelector('.drawer__inner') || cartDrawer;
        var newInner    = parsed.querySelector('#CartDrawer .drawer__inner') ||
                          parsed.querySelector('.drawer__inner') ||
                          parsed.querySelector('.shopify-section');
        if (newInner) {
          drawerInner.innerHTML = newInner.innerHTML;
          var updatedDrawer = parsed.querySelector('#CartDrawer');
          if (updatedDrawer) {
            cartDrawer.classList.toggle('is-empty', updatedDrawer.classList.contains('is-empty'));
          }
        }
      }

      // ── is-empty state ────────────────────────────────────────────────
      if (mainCartItems) {
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (c) {
            var isEmpty = c.item_count === 0;
            var wrapper = document.querySelector('cart-items');
            if (wrapper) wrapper.classList.toggle('is-empty', isEmpty);
            if (mainCartFooter) mainCartFooter.classList.toggle('is-empty', isEmpty);
          });
      }

      LOG.ok('refreshThemeCart: complete (' + sectionIds.length + ' sections) — no reload');

      // ── Re-enable checkout button ─────────────────────────────────────
      // Dawn's CartItems web component calls disableSubmit() during any cart
      // mutation and only re-enables it after its own internal fetch cycle.
      // Because we replace the DOM via innerHTML, the component never fires
      // its own enableSubmit(), leaving the checkout button permanently
      // disabled. We fix this by:
      //   1. Removing the disabled attribute directly.
      //   2. Dispatching a synthetic change event on the cart form so any
      //      Dawn listeners (note fields, cart attributes) re-validate.
      setTimeout(function () {
        // Remove disabled from all checkout / submit buttons inside cart
        var checkoutBtns = document.querySelectorAll(
          '#main-cart-footer [name="checkout"], ' +
          '#main-cart-footer button[type="submit"], ' +
          '.cart__checkout-button, ' +
          'cart-items button[type="submit"]'
        );
        checkoutBtns.forEach(function (btn) {
          btn.removeAttribute('disabled');
          btn.classList.remove('loading', 'loading--spinner');
        });

        // Nudge Dawn's form validation by dispatching change on the cart form
        var cartForm = document.querySelector('form[action="/cart"]');
        if (cartForm) {
          cartForm.dispatchEvent(new Event('change', { bubbles: true }));
        }

        LOG.ok('refreshThemeCart: checkout button re-enabled');
      }, 100);

      // Hide the arrival overlay NOW — cart content is in the DOM.
      hideArrivalOverlay();
      hideSyncStrip(600);

      // ── Show toast after successful cart update ───────────────────────
      // Not shown during the initial arrival overlay (that case already has
      // a full-screen "Syncing your cart..." message).
      if (!URL_USER_ID) {
        showCartToast(incomingItems || []);
      }

    } catch (err) {
      LOG.warn('refreshThemeCart: section API failed — falling back to reload', err);
      hideArrivalOverlay();
      hideSyncStrip(0);
      window.location.reload();
    }
  }

  // ── Intercept Shopify cart mutations ──────────────────────────────────
  var _origFetch = window.fetch;
  window.fetch = async function () {
    var response = await _origFetch.apply(this, arguments);
    var url = String(arguments[0] || '');
    if (url.includes('/cart/add') || url.includes('/cart/update') ||
        url.includes('/cart/change') || url.includes('/cart/clear')) {
      if (isSyncingFromDO) {
        LOG.info('Fetch interceptor: suppressed ' + url);
      } else {
        var type = url.split('/cart/')[1].split('.')[0].split('?')[0];
        LOG.info('Fetch interceptor: user mutation -> ' + type);
        setTimeout(function () { scheduleSync('mutation:' + type); }, 300);
      }
    }
    return response;
  };

  // Re-stamp + push on Shopify checkout button click
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest('[name="checkout"], .cart__checkout-button, [href="/checkout"]')) {
      LOG.info('Checkout click — stamping attribute + pushing cart');
      writeUserIdToCartAttributes();
      doSync('checkout_started');
    }
  });

  // ── WebSocket ─────────────────────────────────────────────────────────
  function connect() {
    if (isStopped) return;
    var url = WS_URL + '?uId=' + encodeURIComponent(USER_ID) + '&sourceId=' + encodeURIComponent(SOURCE_ID);
    LOG.info('Connecting WS ' + url);
    if (ws) { try { ws.close(); } catch (e) {} }
    ws = new WebSocket(url);

    ws.addEventListener('open', function () {
      LOG.ok('WS connected ts:' + getLocalTs() + ' hash:' + getLocalHash());
      reconnectDelay = 2000;
      scheduleSync('page_load');
    });

    ws.addEventListener('message', function (event) {
      try {
        var msg = JSON.parse(event.data);
        if (msg.type === 'cart_init' || msg.type === 'cart_update') {
          handleIncoming(msg);
        } else if (msg.type === 'checkout_completed') {
          LOG.ok('checkout_completed — stopping WS');
          isStopped = true;
          ws.close(1000, 'Checkout completed');
        } else if (msg.type !== 'pong') {
          LOG.warn('Unknown WS type: ' + msg.type);
        }
      } catch (e) { LOG.error('WS parse error', e); }
    });

    ws.addEventListener('close', function (event) {
      if (event.code === 1000) return;
      setTimeout(connect, reconnectDelay);
      reconnectDelay = Math.min(reconnectDelay * 1.5, 30000);
    });

    ws.addEventListener('error', function (e) { LOG.error('WS error', e); ws.close(); });
  }

  // ── Patch all WordPress links — append ?uId= on click ────────────────
  var _wpLinksPatched = false;
  function patchWPLinks() {
    if (_wpLinksPatched) return;
    _wpLinksPatched = true;
    document.addEventListener('click', function (e) {
      var link = e.target.closest('a');
      if (!link) return;

      var href = link.getAttribute('href');
      if (!href) return;

      var isWPLink = href.startsWith(WP_DOMAIN) ||
                     href.startsWith(WP_DOMAIN.replace('https://', '//'));
      if (!isWPLink) return;

      if (href.includes('uId=') || href.includes('userId=')) return;

      e.preventDefault();

      var currentUserId = localStorage.getItem(LS_USER_ID_KEY) || USER_ID;
      var target = new URL(href);
      target.searchParams.set('uId', currentUserId);

      LOG.info('WP link patched uId:' + currentUserId + ' -> ' + target.toString());
      window.location.href = target.toString();
    });

    LOG.ok('patchWPLinks: active for ' + WP_DOMAIN);
  }

  // ── Overlay / Strip / Toast ───────────────────────────────────────────

  var _overlayEl   = null;
  var _stripEl     = null;
  var _stripTimer  = null;
  var _toastEl     = null;
  var _toastTimer  = null;
  var TOAST_DURATION_MS = 4000; // auto-dismiss after 4s

  function injectOverlayStyles() {
    if (document.getElementById('cs-overlay-styles')) return;
    var s = document.createElement('style');
    s.id = 'cs-overlay-styles';
    s.textContent = [
      // ── Full-screen arrival overlay ───────────────────────────────────
      '#cs-arrival-overlay {',
      '  position: fixed; inset: 0; z-index: 99999;',
      '  background: rgb(var(--color-background, 255,255,255));',
      '  display: flex; flex-direction: column;',
      '  align-items: center; justify-content: center; gap: 1.6rem;',
      '  opacity: 1; transition: opacity 0.4s ease;',
      '}',
      '#cs-arrival-overlay.cs-fade-out { opacity: 0; pointer-events: none; }',
      '#cs-arrival-overlay svg.spinner {',
      '  width: 4rem; height: 4rem;',
      '  animation: rotator 1.4s linear infinite;',
      '}',
      // SVG ring (used by both early-paint and late overlays)
      '@keyframes cs-spin-full{to{transform:rotate(360deg)}}',
      '@keyframes cs-dash{0%{stroke-dashoffset:220}50%{stroke-dashoffset:55}100%{stroke-dashoffset:220}}',
      '#cs-arrival-overlay .cs-ring-svg{width:5.2rem;height:5.2rem;overflow:visible;}',
      '#cs-arrival-overlay .cs-ring-track{opacity:0.12;}',
      '#cs-arrival-overlay .cs-ring-fill{',
      '  stroke-dasharray:220;stroke-dashoffset:220;',
      '  transform-origin:50% 50%;transform:rotate(-90deg);',
      '  animation:cs-spin-full 1.4s linear infinite,cs-dash 1.6s ease-in-out infinite;',
      '}',
      '#cs-arrival-overlay .cs-overlay-text {',
      '  font-family: var(--font-body-family, sans-serif);',
      '  font-size: 1.4rem;',
      '  color: rgb(var(--color-foreground, 18,18,18));',
      '  opacity: 0.65; letter-spacing: 0.04em;',
      '}',
      // ── Top-bar sync strip ────────────────────────────────────────────
      '#cs-sync-strip {',
      '  position: fixed; top: 0; left: 0; right: 0; z-index: 99998;',
      '  height: 3px;',
      '  background: rgba(var(--color-foreground, 18,18,18), 0.12);',
      '  overflow: hidden; transition: opacity 0.3s ease;',
      '}',
      '#cs-sync-strip::after {',
      '  content: ""; position: absolute; top: 0; left: -40%;',
      '  width: 40%; height: 100%;',
      '  background: rgb(var(--color-foreground, 18,18,18));',
      '  animation: cs-strip-slide 1.1s ease-in-out infinite;',
      '}',
      '#cs-sync-strip.cs-strip-hidden { opacity: 0; pointer-events: none; }',
      '@keyframes cs-strip-slide { 0% { left: -40%; } 100% { left: 120%; } }',
      // ── Cart update toast — matches Dawn popup style ──────────────────
      '#cs-toast {',
      '  position: fixed;',
      '  bottom: 2.4rem;',
      '  right: 2.4rem;',
      '  z-index: 99999;',
      '  display: flex;',
      '  align-items: flex-start;',
      '  gap: 1.2rem;',
      '  padding: 1.6rem 2rem;',
      '  min-width: 26rem;',
      '  max-width: 36rem;',
      '  background: rgb(var(--color-background, 255,255,255));',
      '  color: rgb(var(--color-foreground, 18,18,18));',
      '  font-family: var(--font-body-family, sans-serif);',
      '  font-size: 1.3rem;',
      '  border-radius: var(--popup-corner-radius, 0.4rem);',
      '  border: var(--popup-border-width, 1px) solid rgba(var(--color-foreground, 18,18,18), var(--popup-border-opacity, 0.1));',
      '  box-shadow: var(--popup-shadow-horizontal-offset, 0px) var(--popup-shadow-vertical-offset, 4px) var(--popup-shadow-blur-radius, 16px) rgba(var(--color-shadow, 0,0,0), var(--popup-shadow-opacity, 0.1));',
      '  transform: translateY(1.2rem);',
      '  opacity: 0;',
      '  transition: opacity 0.25s ease, transform 0.25s ease;',
      '  pointer-events: none;',
      '}',
      '#cs-toast.cs-toast--visible {',
      '  opacity: 1; transform: translateY(0); pointer-events: auto;',
      '}',
      '#cs-toast.cs-toast--hiding {',
      '  opacity: 0; transform: translateY(1.2rem);',
      '}',
      // checkmark icon
      '#cs-toast .cs-toast__icon {',
      '  flex-shrink: 0; width: 1.8rem; height: 1.8rem; margin-top: 0.1rem;',
      '}',
      // text body
      '#cs-toast .cs-toast__body { flex: 1; line-height: 1.5; }',
      '#cs-toast .cs-toast__title {',
      '  font-weight: 600; margin: 0 0 0.2rem;',
      '  font-size: 1.3rem; letter-spacing: 0.02em;',
      '}',
      '#cs-toast .cs-toast__message {',
      '  margin: 0; opacity: 0.65; font-size: 1.2rem;',
      '}',
      // close button
      '#cs-toast .cs-toast__close {',
      '  flex-shrink: 0; background: none; border: none; cursor: pointer;',
      '  padding: 0; margin-top: -0.2rem;',
      '  color: rgb(var(--color-foreground, 18,18,18));',
      '  opacity: 0.4; line-height: 1; transition: opacity 0.15s ease;',
      '}',
      '#cs-toast .cs-toast__close:hover { opacity: 0.85; }',
      // auto-dismiss progress bar
      '#cs-toast .cs-toast__progress {',
      '  position: absolute; bottom: 0; left: 0;',
      '  height: 2px; width: 100%;',
      '  background: rgba(var(--color-foreground, 18,18,18), 0.15);',
      '  border-radius: 0 0 var(--popup-corner-radius, 0.4rem) var(--popup-corner-radius, 0.4rem);',
      '  transform-origin: left; transform: scaleX(1);',
      '}',
      // mobile: full-width at bottom
      '@media screen and (max-width: 749px) {',
      '  #cs-toast {',
      '    left: 1.6rem; right: 1.6rem;',
      '    min-width: unset; max-width: unset; bottom: 1.6rem;',
      '  }',
      '}',
    ].join('\n');
    document.head.appendChild(s);
  }

  function spinnerSVG(size) {
    size = size || '4rem';
    return '<svg class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg" style="width:' + size + ';height:' + size + ';" aria-hidden="true">'
      + '<circle class="path" fill="none" stroke-width="6" stroke-linecap="round" cx="33" cy="33" r="30"></circle>'
      + '</svg>';
  }

  // ── Toast: shown after every background cart update ───────────────────
  // Builds a human-readable message from the incoming items list.
  // Auto-dismisses after TOAST_DURATION_MS with a shrinking progress bar.
  function showCartToast(items) {
    injectOverlayStyles();

    // Dismiss any existing toast immediately before showing new one
    if (_toastEl) {
      clearTimeout(_toastTimer);
      if (_toastEl.parentNode) _toastEl.parentNode.removeChild(_toastEl);
      _toastEl = null;
    }

    // Build message
    var title   = 'Cart updated';
    var message = '';
    if (!items || items.length === 0) {
      message = 'Your cart has been cleared.';
    } else if (items.length === 1) {
      message = items[0].productName
        ? '\u2018' + items[0].productName + '\u2019 is in your cart.'
        : '1 item in your cart.';
    } else {
      message = items.length + ' items synced to your cart.';
    }

    // Checkmark SVG icon
    var checkIcon = '<svg class="cs-toast__icon" viewBox="0 0 24 24" fill="none" '
      + 'stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">'
      + '<polyline points="20 6 9 17 4 12"></polyline></svg>';

    // Close button SVG
    var closeIcon = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" '
      + 'stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true">'
      + '<line x1="18" y1="6" x2="6" y2="18"></line>'
      + '<line x1="6" y1="6" x2="18" y2="18"></line></svg>';

    _toastEl = document.createElement('div');
    _toastEl.id = 'cs-toast';
    _toastEl.setAttribute('role', 'status');
    _toastEl.setAttribute('aria-live', 'polite');
    _toastEl.setAttribute('aria-atomic', 'true');
    _toastEl.innerHTML = [
      checkIcon,
      '<div class="cs-toast__body">',
      '  <p class="cs-toast__title">' + title + '</p>',
      '  <p class="cs-toast__message">' + message + '</p>',
      '</div>',
      '<button class="cs-toast__close" aria-label="Dismiss">' + closeIcon + '</button>',
      '<div class="cs-toast__progress"></div>',
    ].join('');

    document.body.appendChild(_toastEl);

    // Close button handler
    _toastEl.querySelector('.cs-toast__close').addEventListener('click', function () {
      hideCartToast(true);
    });

    // Animate in on next frame
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        if (_toastEl) _toastEl.classList.add('cs-toast--visible');
      });
    });

    // Shrink progress bar over TOAST_DURATION_MS
    var progressEl = _toastEl.querySelector('.cs-toast__progress');
    if (progressEl) {
      progressEl.style.transition = 'transform ' + TOAST_DURATION_MS + 'ms linear';
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (progressEl) progressEl.style.transform = 'scaleX(0)';
        });
      });
    }

    // Auto-dismiss
    _toastTimer = setTimeout(function () { hideCartToast(false); }, TOAST_DURATION_MS);

    LOG.ok('Toast shown: ' + title + ' — ' + message);
  }

  function hideCartToast(immediate) {
    if (!_toastEl) return;
    clearTimeout(_toastTimer);
    _toastEl.classList.remove('cs-toast--visible');
    _toastEl.classList.add('cs-toast--hiding');
    var el = _toastEl;
    var delay = immediate ? 260 : 260;
    setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, delay);
    _toastEl = null;
    LOG.ok('Toast hidden');
  }

  // ── Arrival overlay ───────────────────────────────────────────────────
  function showArrivalOverlay() {
    // If the early overlay was already injected pre-paint, reuse it.
    if (!_overlayEl) {
      _overlayEl = document.getElementById('cs-arrival-overlay');
    }
    if (_overlayEl) return; // already showing
    injectOverlayStyles();
    _overlayEl = document.createElement('div');
    _overlayEl.id = 'cs-arrival-overlay';
    _overlayEl.setAttribute('role', 'status');
    _overlayEl.setAttribute('aria-label', 'Loading your cart\u2026');
    _overlayEl.innerHTML = '<svg class="cs-ring-svg" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">'
      + '<circle class="cs-ring-track" cx="40" cy="40" r="35" stroke="rgb(var(--color-foreground,18,18,18))" stroke-width="5"/>'
      + '<circle class="cs-ring-fill" cx="40" cy="40" r="35" stroke="rgb(var(--color-foreground,18,18,18))" stroke-width="5" stroke-linecap="round"/>'
      + '</svg>'
      + '<p class="cs-overlay-text">Please wait, loading your cart\u2026</p>';
    document.body.appendChild(_overlayEl);
    LOG.ok('Arrival overlay shown (late)');
  }

  function hideArrivalOverlay() {
    // Pick up either the pre-paint early overlay or one created by showArrivalOverlay()
    if (!_overlayEl) _overlayEl = document.getElementById('cs-arrival-overlay');
    if (!_overlayEl) return;
    _overlayEl.classList.add('cs-fade-out');
    var el = _overlayEl;
    _overlayEl = null;
    setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 420);
    // Clean up the early-injected style tag if present
    var earlyStyle = document.getElementById('cs-early-overlay-style');
    if (earlyStyle && earlyStyle.parentNode) earlyStyle.parentNode.removeChild(earlyStyle);
    LOG.ok('Arrival overlay hidden');
  }

  // ── Sync strip ────────────────────────────────────────────────────────
  function showSyncStrip() {
    injectOverlayStyles();
    if (_stripEl) {
      clearTimeout(_stripTimer);
      _stripEl.classList.remove('cs-strip-hidden');
      return;
    }
    _stripEl = document.createElement('div');
    _stripEl.id = 'cs-sync-strip';
    _stripEl.setAttribute('role', 'status');
    _stripEl.setAttribute('aria-label', 'Updating cart\u2026');
    document.body.appendChild(_stripEl);
    LOG.ok('Sync strip shown');
  }

  function hideSyncStrip(delay) {
    if (!_stripEl) return;
    clearTimeout(_stripTimer);
    _stripTimer = setTimeout(function () {
      if (!_stripEl) return;
      _stripEl.classList.add('cs-strip-hidden');
      var el = _stripEl;
      setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 320);
      _stripEl = null;
      LOG.ok('Sync strip hidden');
    }, delay || 600);
  }

  // ── Boot helpers ──────────────────────────────────────────────────────
  function boot() {
    LOG.ok('boot() URL_USER_ID:' + (URL_USER_ID || 'none'));

    if (URL_USER_ID) {
      LOG.ok('Arrived via WP — early overlay already shown, writing attribute, fetching DO cart, connecting WS');
      showArrivalOverlay();
      writeUserIdToCartAttributes()
        .then(function () { return fetchCartFromDO(); })
        .then(function () {
          if (!isApplying) hideArrivalOverlay();
          connect();
        })
        .catch(function (err) {
          LOG.error('Arrival sequence failed', err);
          hideArrivalOverlay();
          connect();
        });
    } else {
      showSyncStrip();
      writeUserIdToCartAttributes();
      connect();
    }

    patchWPLinks();
  }

  // ── Boot ──────────────────────────────────────────────────────────────
  document.addEventListener('DOMContentLoaded', function () {
    LOG.ok('DOMContentLoaded — running boot()');
    boot();
  });

  // ── bfcache recovery ──────────────────────────────────────────────────
  // pageshow with persisted=true means the browser restored from bfcache
  // (back button). location.reload() can be ignored by bfcache in some
  // browsers — reassigning location.href is more reliable as it forces a
  // full navigation. We also inject the overlay before the reload so the
  // user sees the loading state immediately rather than a flash of empty cart.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    if (!URL_USER_ID) return;
    if (window.location.pathname.indexOf('/cart') === -1) return;

    LOG.ok('pageshow persisted=true on cart+uId — showing overlay and reloading');

    // Inject overlay immediately so there is no empty-cart flash before reload
    var s = document.createElement('style');
    s.textContent = '#cs-bf-overlay{position:fixed;inset:0;z-index:99999;'
      + 'background:rgb(var(--color-background,255,255,255));'
      + 'display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1.6rem;}'
      + '@keyframes cs-bf-spin{to{transform:rotate(360deg)}}'
      + '@keyframes cs-bf-dash{0%{stroke-dashoffset:220}50%{stroke-dashoffset:55}100%{stroke-dashoffset:220}}'
      + '#cs-bf-overlay .cs-bf-ring{width:5.2rem;height:5.2rem;overflow:visible;}'
      + '#cs-bf-overlay .cs-bf-track{opacity:0.12;}'
      + '#cs-bf-overlay .cs-bf-fill{stroke-dasharray:220;stroke-dashoffset:220;'
      + 'transform-origin:50% 50%;transform:rotate(-90deg);'
      + 'animation:cs-bf-spin 1.4s linear infinite,cs-bf-dash 1.6s ease-in-out infinite;}'
      + '#cs-bf-overlay p{font-family:var(--font-body-family,sans-serif);font-size:1.4rem;'
      + 'color:rgb(var(--color-foreground,18,18,18));opacity:0.65;letter-spacing:0.04em;margin:0;}';
    document.head.appendChild(s);

    var el = document.createElement('div');
    el.id = 'cs-bf-overlay';
    el.setAttribute('role', 'status');
    el.innerHTML = '<svg class="cs-bf-ring" viewBox="0 0 80 80" fill="none" aria-hidden="true">'
      + '<circle class="cs-bf-track" cx="40" cy="40" r="35" stroke="rgb(var(--color-foreground,18,18,18))" stroke-width="5"/>'
      + '<circle class="cs-bf-fill" cx="40" cy="40" r="35" stroke="rgb(var(--color-foreground,18,18,18))" stroke-width="5" stroke-linecap="round"/>'
      + '</svg>'
      + '<p>Please wait, loading your cart\u2026</p>';
    document.documentElement.appendChild(el);

    // Use href assignment — more reliable than reload() for breaking bfcache
    window.location.href = window.location.href;
  });

  window.CartSyncShopify = {
    userId:      USER_ID,
    sourceId:    SOURCE_ID,
    push:        function () { doSync('manual'); },
    reconnect:   connect,
    fetchFromDO: fetchCartFromDO,
    refreshUI:   function () { return refreshThemeCart([]); },
    toast:       function (msg) { showCartToast(msg ? [{ productName: msg }] : []); },
    hash: function () {
      fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
        LOG.info('Shopify cart hash: ' + computeCartHash(c.items || []));
      });
    },
    status: function () {
      LOG.info('status', {
        wsState:         ws ? ['CONNECTING','OPEN','CLOSING','CLOSED'][ws.readyState] : 'null',
        userId:          USER_ID,
        sourceId:        SOURCE_ID,
        urlUserId:       URL_USER_ID,
        localTs:         getLocalTs(),
        localHash:       getLocalHash(),
        isSyncingFromDO: isSyncingFromDO,
        isApplying:      isApplying,
      });
    },
  };

  LOG.ok('Ready — CartSyncShopify.status() | .hash() | .fetchFromDO() | .refreshUI() | .toast()');

})();
